<template>
  <div
    v-if="visible"
    type="button"
    class="flex max-h-[60px] items-center justify-between gap-2 overflow-hidden rounded-full bg-primary p-0 text-base"
  >
    <p class="inline-block max-w-1/2">
      <slot name="title" />
    </p>
    <div class="min-w-1/2">
      <slot name="button" />
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { Nullable } from 'ts-helpers'
import { onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { getElementByGlobalId, GlobalIds } from '../config'

type PropType = {
  title: string
  visible: boolean
  observableElementId?: GlobalIds
}
type EmitType = {
  (e: 'update:visible', value: boolean): void
}

const props = defineProps<PropType>()
const emit = defineEmits<EmitType>()

const observableElement = ref<Nullable<HTMLElement>>(null)
let count = 0
let intervalId: NodeJS.Timeout | null = null

if (props.observableElementId) {
  onMounted(() => {
    if (!props.observableElementId) return

    intervalId = setInterval(() => {
      if (count === 20 || observableElement.value) {
        intervalId && clearInterval(intervalId)
      } else {
        observableElement.value = getElementByGlobalId(props.observableElementId!)
      }
    }, 1000)
  })
  watch(
    () => observableElement.value,
    () => initObserver()
  )

  // IntersectionObserver
  let observer: Nullable<IntersectionObserver> = null
  const intersectionObserverOptions: IntersectionObserverInit = {
    rootMargin: '0px',
    threshold: 0.1
  }
  const initObserver = () => {
    if (!observableElement.value) {
      // из за немгновенной записи ссылки на html,
      // убираем моргание кнопки
      emit('update:visible', false)

      return
    }

    observer = new IntersectionObserver(intersectionObserverCallBack, intersectionObserverOptions)
    observer.observe(observableElement.value)
  }

  const intersectionObserverCallBack: IntersectionObserverCallback = function (
    entries: IntersectionObserverEntry[]
  ) {
    entries.forEach((entry) => {
      const { isIntersecting } = entry

      emit('update:visible', !isIntersecting)
    })
  }

  const destroyObserver = () => {
    if (!observer || !observableElement.value) return

    observer.unobserve(observableElement.value)
    observer = null
  }

  onBeforeUnmount(() => {
    destroyObserver()
    intervalId && clearInterval(intervalId)
  })
}
</script>
